import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getSubject } from "@/lib/api";
import { SUBJECT_COLORS } from "@/lib/subjectMeta";
import {
  BookOpen, Atom, Calculator, FlaskConical, Globe, Languages,
  Music, Palette, Cpu, Leaf, Brain, Heart, ArrowLeft
} from "lucide-react";
import NotesTab from "@/components/subject/NotesTab";
import RevisionBooksTab from "@/components/subject/RevisionBooksTab";
import FlashcardsTab from "@/components/subject/FlashcardsTab";
import PodcastsTab from "@/components/subject/PodcastsTab";
import ChatTab from "@/components/subject/ChatTab";

const ICONS = { BookOpen, Atom, Calculator, FlaskConical, Globe, Languages, Music, Palette, Cpu, Leaf, Brain, Heart };

const TABS = [
  { key: "notes", label: "Notes" },
  { key: "books", label: "Revision Books" },
  { key: "flashcards", label: "Flashcards" },
  { key: "podcasts", label: "Podcasts" },
  { key: "chat", label: "AI Tutor" },
];

export default function SubjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [subject, setSubject] = useState(null);
  const [tab, setTab] = useState("notes");

  useEffect(() => {
    getSubject(id).then(setSubject).catch(() => navigate("/"));
  }, [id, navigate]);

  if (!subject) return <div className="font-bold">Loading…</div>;

  const Icon = ICONS[subject.icon] || BookOpen;
  const color = SUBJECT_COLORS[subject.color] || SUBJECT_COLORS.primary;

  return (
    <div className="max-w-7xl mx-auto" data-testid="subject-detail">
      <button
        data-testid="back-to-dashboard"
        onClick={() => navigate("/")}
        className="nb-button px-3 py-2 mb-6 inline-flex items-center gap-2 text-sm"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {/* Hero header */}
      <div className={`nb-border rounded-3xl p-6 sm:p-10 mb-8 nb-shadow-lg ${color.chip} dark:${color.bg}`}>
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl nb-border bg-white dark:bg-zinc-900 flex items-center justify-center nb-shadow">
            <Icon className="w-9 h-9 text-black dark:text-white" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-caps text-black">Subject</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-black truncate">
              {subject.name}
            </h1>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`nb-border rounded-xl px-4 py-2 font-bold text-sm sm:text-base whitespace-nowrap transition-all ${
              tab === t.key
                ? "bg-black text-white dark:bg-white dark:text-black nb-shadow"
                : "bg-white dark:bg-zinc-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "notes" && <NotesTab subject={subject} />}
      {tab === "books" && <RevisionBooksTab subject={subject} />}
      {tab === "flashcards" && <FlashcardsTab subject={subject} />}
      {tab === "podcasts" && <PodcastsTab subject={subject} />}
      {tab === "chat" && <ChatTab subject={subject} />}
    </div>
  );
}
